// 🔴 The `.ts` extension is REQUIRED, not stylistic. This module is imported by
// `supabase/functions/sync-social-analytics`, which Supabase bundles with Deno,
// and Deno does not resolve extensionless relative specifiers. Without it the
// deploy fails with `Module not found ".../src/lib/platforms/registry"`.
// esbuild and Vite both resolve it fine, so a local bundle reproduction will
// NOT catch this — only a real deploy does. `allowImportingTsExtensions` is on
// in every tsconfig here, so the extension costs the app nothing.
import { PLATFORMS, type PlatformId } from './registry.ts';

/**
 * Connection capabilities are intentionally separate from platform identity,
 * placement publishing, and media specs. A provider can be configured for
 * OAuth before its connection schema is live, and a persisted connection does
 * not imply that the generic social analytics dispatcher can refresh it.
 */
export const OAUTH_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'meta-ads',
  'google-ads',
  'linkedin-ads',
  'linkedin',
  'shopify',
  'tiktok',
  'pinterest',
  'youtube',
  'google-analytics',
  'google-business',
  'slack',
] as const;

export type OAuthPlatform = (typeof OAUTH_PLATFORM_IDS)[number];

/**
 * Platforms that the current `user_connections` constraint can persist.
 * Google Business stays in the OAuth configuration layer behind its feature
 * flag until its database migration and generated types ship.
 */
export const PERSISTED_CONNECTION_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'meta-ads',
  'google-ads',
  'linkedin-ads',
  'linkedin',
  'shopify',
  'tiktok',
  'pinterest',
  'youtube',
  'google-analytics',
  'slack',
] as const satisfies readonly OAuthPlatform[];

export type PersistedConnectionPlatform = (typeof PERSISTED_CONNECTION_PLATFORM_IDS)[number];

/** The narrow command set owned by `sync-social-analytics`. */
export const SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'tiktok',
  'linkedin',
  'youtube',
  'shopify',
  'pinterest',
] as const satisfies readonly PersistedConnectionPlatform[];

export type SocialAnalyticsSyncPlatform = (typeof SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS)[number];

/**
 * Analytics capability axis — the single source of truth for "can this
 * connection produce dashboard analytics". Task 7's `analytics-capability.ts`
 * derives its capability facts from HERE (never a parallel map), and Task 8's
 * Settings disclosure reads the same function so Settings can never contradict
 * Analytics.
 *
 * Keyed by platform plus, where the platform's API draws the line, account_type
 * (LinkedIn: member and Company Page analytics use different approved scopes).
 * Capability = a STABLE product fact, never a transient data-state
 * ("pending first sync" belongs to the read-path status line, not here).
 */
export type AnalyticsCapabilityKind = 'available' | 'unavailable';

/** LinkedIn is the only platform whose analytics gate turns on account_type. */
export type LinkedInAnalyticsAccountType = 'person' | 'organization';

/**
 * Discriminated on purpose: `unavailable` REQUIRES a reason. The UI renders an
 * unavailable axis as a human sentence and nothing else — an optional `reason`
 * let a non-publishable platform ship a blank line (the old `Partial<Record<…>>`
 * lookup returned undefined for any platform missing from the reason map). The
 * type now makes that unrepresentable: adding a non-publishable platform without
 * writing its reason is a compile error, not an empty row in production.
 */
export type AnalyticsCapabilityResult =
  { capability: 'available'; reason?: never } | { capability: 'unavailable'; reason: string };

export const LINKEDIN_PERSONAL_ANALYTICS_REASON =
  'Personal profile analytics require LinkedIn member-analytics permission and a reconnect.';

/**
 * Slack is a content-ingestion/send connection, not a measurement source — it
 * has no analytics concept at all (unlike LinkedIn, which HAS analytics just
 * gated by account_type). Without this case it would fall through to the
 * default `{ capability: 'available' }` below and Settings would falsely
 * claim Slack produces dashboard analytics.
 */
export const SLACK_ANALYTICS_REASON =
  'Slack is a content connection, not a measurement source — it has no analytics.';

/**
 * Resolve whether a platform (with an optional account_type) can produce
 * analytics. A LinkedIn personal profile remains unavailable until the separate
 * member-analytics permissions and provider receipt pass; Company Page analytics
 * use their organization grant. Meta Ads
 * is also available here: it has a distinct selected-account fetcher and
 * source-native tables. Whether an individual connection has selected an ad
 * account or completed its first sync is a transient read-path state, not a
 * permanent capability claim.
 */
export function analyticsCapability(
  platform: string,
  accountType?: string | null,
): AnalyticsCapabilityResult {
  if (platform === 'linkedin') {
    return accountType === 'organization'
      ? { capability: 'available' }
      : { capability: 'unavailable', reason: LINKEDIN_PERSONAL_ANALYTICS_REASON };
  }
  if (platform === 'slack') {
    return { capability: 'unavailable', reason: SLACK_ANALYTICS_REASON };
  }
  return { capability: 'available' };
}

/**
 * Generic capability-axis result, structurally shared with
 * {@link AnalyticsCapabilityResult}. Every axis (publishing / analytics /
 * engagement inbox) reports one STABLE product fact — never a transient
 * data-state — so Settings and the Analytics read-path can never contradict
 * each other. An `available` axis carries NO copy at all and renders nothing;
 * only an `unavailable` axis has something to say, and the union forces it to
 * say it. See {@link getConnectionCaveats} for how the Settings row consumes it.
 */
export type CapabilityKind = AnalyticsCapabilityKind;

export type CapabilityResult = AnalyticsCapabilityResult;

/** Locked capability copy — stable facts, never "coming soon" happy-talk. */
export const PINTEREST_PUBLISHING_REASON = 'Pinterest is analytics-only for now.';
export const YOUTUBE_PUBLISHING_REASON =
  'YouTube connects for channel analytics today — publishing comes later.';

/**
 * The reason every non-publishable registry channel shows. Typed as a TOTAL
 * record over the non-publishable set rather than `Partial<Record<PlatformId>>`,
 * so making a platform `publishable: false` without writing its sentence fails
 * to compile instead of rendering an empty caveat.
 */
type NonPublishablePlatformId = {
  [K in PlatformId]: (typeof PLATFORMS)[K]['publishable'] extends false ? K : never;
}[PlatformId];

const PUBLISHING_UNAVAILABLE_REASONS: Record<NonPublishablePlatformId, string> = {
  pinterest: PINTEREST_PUBLISHING_REASON,
  youtube: YOUTUBE_PUBLISHING_REASON,
};

/**
 * Publishing axis — meaningful ONLY for the six social channels the platform
 * registry knows how to publish to (its keys ARE that set: measurement
 * connections like Google Analytics / Shopify and file connections like Google
 * Drive are not channels, so they get no publishing line). Returns null when
 * the axis does not apply, which suppresses the row rather than printing a
 * uniform dash-grid. `publishable` is read straight from the registry so this
 * axis can never drift from the real posting capability.
 */
export function publishingCapability(platform: string): CapabilityResult | null {
  if (!(platform in PLATFORMS)) return null;
  const entry = PLATFORMS[platform as PlatformId];
  if (entry.publishable && entry.oauthReady) return { capability: 'available' };
  if (!entry.publishable) {
    return {
      capability: 'unavailable',
      reason: PUBLISHING_UNAVAILABLE_REASONS[entry.id as NonPublishablePlatformId],
    };
  }
  // Publishable channel whose OAuth is not live yet — a setup gap, not a
  // permanent product boundary, so it gets its own sentence.
  return {
    capability: 'unavailable',
    reason: `${entry.label} publishing isn't connected yet.`,
  };
}

/**
 * NOTE: the user-facing description of a connection — what data it actually
 * gives Mora, and what it pointedly does not — lives in
 * `connection-data-inventory.ts`, NOT here. This module stays the machine-
 * readable capability matrix (can this platform publish / report analytics /
 * feed the inbox) that other code branches on. Keep the prose in one place: an
 * earlier version of this file grew its own parallel copy strings, which is
 * exactly how two surfaces start telling a user different things.
 */

/**
 * Engagement inbox is a Meta-only capability today (Instagram + Facebook
 * comment/DM ingestion); Google Business joins it only where that connection is
 * present. Every other platform has no inbox surface, so this returns null and
 * the axis is not rendered — the plan's "Inbox axis ONLY where meaningful, no
 * uniform dash-grid" rule.
 *
 * LinkedIn is deliberately absent until Community Management API approval AND
 * the 48h / no-commenter-profile storage rules are built. LinkedIn engagement
 * is permanently AI-ineligible (`isAiEligibleEngagement`), even once it joins.
 */
export const ENGAGEMENT_INBOX_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'google-business',
] as const satisfies readonly OAuthPlatform[];

export function inboxCapability(platform: string): CapabilityResult | null {
  if (!(ENGAGEMENT_INBOX_PLATFORM_IDS as readonly string[]).includes(platform)) return null;
  return { capability: 'available' };
}

/** The stable identity of each capability axis rendered in the disclosure. */
export type CapabilityAxisId = 'publishing' | 'analytics' | 'inbox';

export interface CapabilityAxis {
  id: CapabilityAxisId;
  label: string;
  result: CapabilityResult;
}

/**
 * The ordered capability axes to render for a connected account. Publishing and
 * Analytics appear for every channel/measurement connection they apply to;
 * Engagement inbox appears only for inbox-capable platforms. A platform with no
 * applicable axis (e.g. Google Drive) yields an empty array, which the row uses
 * to suppress the disclosure entirely. LinkedIn's analytics availability turns
 * on `accountType` (personal profiles expose no analytics API — Company Pages
 * only), the single account_type-conditional fact in the matrix.
 */
export function getConnectionCapabilityAxes(
  platform: string,
  accountType?: string | null,
): CapabilityAxis[] {
  const axes: CapabilityAxis[] = [];

  const publishing = publishingCapability(platform);
  if (publishing) axes.push({ id: 'publishing', label: 'Publishing', result: publishing });

  if ((OAUTH_PLATFORM_IDS as readonly string[]).includes(platform)) {
    axes.push({
      id: 'analytics',
      label: 'Analytics',
      result: analyticsCapability(platform, accountType),
    });
  }

  const inbox = inboxCapability(platform);
  if (inbox) axes.push({ id: 'inbox', label: 'Engagement inbox', result: inbox });

  return axes;
}

export function isPersistedConnectionPlatform(
  platform: unknown,
): platform is PersistedConnectionPlatform {
  return (
    typeof platform === 'string' &&
    (PERSISTED_CONNECTION_PLATFORM_IDS as readonly string[]).includes(platform)
  );
}

export function isSocialAnalyticsSyncPlatform(
  platform: unknown,
): platform is SocialAnalyticsSyncPlatform {
  return (
    typeof platform === 'string' &&
    (SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS as readonly string[]).includes(platform)
  );
}

/**
 * An omitted platform list means "all supported commands". A supplied list
 * must contain at least one supported command; callers can reject an empty
 * result rather than accidentally turning an invalid request into a full sync.
 */
export function parseSocialAnalyticsSyncPlatforms(
  rawPlatforms: unknown,
): SocialAnalyticsSyncPlatform[] | null {
  if (rawPlatforms === undefined) return [...SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS];
  if (!Array.isArray(rawPlatforms)) return null;

  const requested = new Set(rawPlatforms.map((platform) => String(platform).toLowerCase().trim()));
  return SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS.filter((platform) => requested.has(platform));
}
